/**
 * Accounts, members, cohorts and invitations — the ownership layer.
 *
 * The shape of it, in one paragraph: an **account** is the owning organisation
 * (an ICB, a private clinic). One person **owns** it, having created it through
 * registration. The owner invites **clinicians** by email address, creates
 * **cohorts**, and assigns clinicians to them. A clinician assigned to a cohort
 * invites **patients** into it, again by email address.
 *
 * Two properties are load-bearing and are asserted in
 * `services/api/tests/test_access.py` rather than merely described here:
 *
 * 1. **An owner administers; it does not read.** There is no role that grants
 *    sight of scores or comments. An owner who also treats patients assigns
 *    themselves to a cohort like anybody else, and is then reading as a
 *    clinician rather than as an owner.
 * 2. **A cohort assignment is data, not a claim.** Assignments change; access
 *    tokens live thirty minutes. Carrying cohorts in the token would mean a
 *    withdrawn assignment stayed live for the remainder of that window.
 *
 * See `docs/tenancy.md` for the isolation argument in the form a buyer asks
 * for it.
 */

import type { IsoInstant } from './types';

/**
 * `pending` until the owner has verified their email address. Invitations
 * cannot be sent from a pending account — otherwise registering with somebody
 * else's address would let you send mail that appears to come from them.
 */
export type AccountStatus = 'pending' | 'active';

export interface Account {
  readonly accountId: string;
  readonly name: string;
  /**
   * The number patients are told to call for help before their next session.
   * Absent only on an account registered before it was asked for.
   */
  readonly contactPhone?: string;
  readonly status: AccountStatus;
  readonly createdAt: IsoInstant;
}

/**
 * `owner` is set once, at registration, and never afterwards — it is an
 * immutable claim on the token, so there is no path by which a clinician
 * becomes an owner by editing an attribute. Transferring ownership is a
 * feature that does not exist yet; when it does, it needs to be a deliberate
 * flow rather than a mutable field.
 */
export type MemberRole = 'owner' | 'clinician';

/**
 * `suspended` rather than deleted. The membership record is what proves a
 * clinician was ever in the account, which is exactly the sort of thing an
 * information-governance question turns on later.
 */
export type MemberStatus = 'active' | 'suspended';

export interface Member {
  readonly userId: string;
  readonly accountId: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: MemberRole;
  readonly status: MemberStatus;
  readonly joinedAt: IsoInstant;
}

/** A group of patients on a course, and the clinicians who run it. */
export interface Cohort {
  readonly cohortId: string;
  readonly accountId: string;
  readonly name: string;
  readonly createdAt: IsoInstant;
  /** Clinicians assigned to it. Present on the admin listing, not on `/my/cohorts`. */
  readonly clinicians?: readonly CohortClinician[];
  /** Patients invited into it. Admin listing only, and a count rather than a roster. */
  readonly patientCount?: number;
}

/** Just enough of a member to render an assignment row. */
export interface CohortClinician {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
}

/**
 * `verification` confirms an owner's address at registration; `patient_reset`
 * restores an existing patient's access. Neither is an invitation to join, and
 * the API refuses each at any route but its own.
 */
export type InviteKind = 'clinician' | 'patient' | 'verification' | 'patient_reset';

/**
 * `revoked` is distinct from expiry: an owner cancelling an invitation is a
 * decision, and the record of it should not look the same as one that simply
 * timed out.
 */
export type InviteStatus = 'pending' | 'accepted' | 'revoked';

/**
 * An invitation, as the admin screen sees it.
 *
 * **The token is not here, and never travels to a client.** Only its hash is
 * stored, and the readable half exists in exactly one place: the email that was
 * sent. An invite token is a bearer credential to health data — treating it as
 * one at rest is the entire reason for that split.
 */
export interface Invite {
  readonly inviteId: string;
  readonly accountId: string;
  readonly kind: InviteKind;
  readonly email: string;
  /** Set on patient invitations, and on clinician invitations pre-assigned to a cohort. */
  readonly cohortId: string | null;
  readonly status: InviteStatus;
  readonly invitedBy: string;
  readonly createdAt: IsoInstant;
  readonly expiresAt: IsoInstant;
}

/**
 * How long an invitation stays valid. Mirrors `inviteExpiryMs` in
 * `contracts/rules.json`; `contracts.test.ts` asserts they agree.
 */
export const INVITE_EXPIRY_MS = 604_800_000;

/** e.g. "in 6 days", "in 3 hours", "expired" — the admin screen's expiry column. */
export function expiryLabel(expiresAt: IsoInstant, now: Date): string {
  const remaining = Date.parse(expiresAt) - now.getTime();
  if (Number.isNaN(remaining) || remaining <= 0) return 'expired';

  const hours = Math.floor(remaining / 3_600_000);
  if (hours < 1) return 'in under an hour';
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;

  const days = Math.floor(hours / 24);
  return `in ${days} days`;
}
